import { supabase } from '../lib/supabase';
import { STRIPE_PRODUCTS, StripeProduct } from '../stripe-config';

export interface SubscriptionData {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export interface OrderData {
  customer_id: string;
  order_id: number;
  checkout_session_id: string;
  payment_intent_id: string;
  amount_subtotal: number;
  amount_total: number;
  currency: string;
  payment_status: string;
  order_status: string;
  order_date: string;
}

export class StripeService {
  /**
   * Create a Stripe checkout session
   */
  static async createCheckoutSession(
    priceId: string,
    mode: 'payment' | 'subscription' = 'subscription'
  ): Promise<{ sessionId: string; url: string }> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('Authentication required');
    }

    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/success?success=true`;
    const cancelUrl = `${baseUrl}/rooms?canceled=true`;

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        price_id: priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data;
  }

  /**
   * Get user's subscription data
   */
  static async getUserSubscription(): Promise<SubscriptionData | null> {
    const { data, error } = await supabase
      .from('stripe_user_subscriptions')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }

    return data;
  }

  /**
   * Get user's order history
   */
  static async getUserOrders(): Promise<OrderData[]> {
    const { data, error } = await supabase
      .from('stripe_user_orders')
      .select('*')
      .order('order_date', { ascending: false });

    if (error) {
      console.error('Error fetching orders:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Get product information by price ID
   */
  static getProductByPriceId(priceId: string): StripeProduct | undefined {
    return STRIPE_PRODUCTS.find(product => product.priceId === priceId);
  }

  /**
   * Check if user has active subscription
   */
  static async hasActiveSubscription(): Promise<boolean> {
    const subscription = await this.getUserSubscription();
    return subscription?.subscription_status === 'active';
  }

  /**
   * Get current subscription plan name
   */
  static async getCurrentPlanName(): Promise<string> {
    const subscription = await this.getUserSubscription();
    
    if (!subscription?.price_id) {
      return 'Free Plan';
    }

    const product = this.getProductByPriceId(subscription.price_id);
    return product?.name || 'Unknown Plan';
  }

  /**
   * Format currency amount
   */
  static formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  }

  /**
   * Format subscription period
   */
  static formatSubscriptionPeriod(start: number | null, end: number | null): string {
    if (!start || !end) return 'N/A';
    
    const startDate = new Date(start * 1000);
    const endDate = new Date(end * 1000);
    
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  }

  /**
   * Check if subscription is ending soon (within 7 days)
   */
  static isSubscriptionEndingSoon(endTimestamp: number | null): boolean {
    if (!endTimestamp) return false;
    
    const endDate = new Date(endTimestamp * 1000);
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return endDate <= sevenDaysFromNow;
  }
}